import React from 'react'

import { faArrowRight } from '@fortawesome/pro-solid-svg-icons/faArrowRight'
import { faSparkles } from '@fortawesome/pro-solid-svg-icons/faSparkles'
import { Box, Container, styled, Typography } from '@mui/material'

import { Button } from '@/components/Button/Button'
import { Icon } from '@/components/Icon/Icon'
import { pxToRem } from '@/shared/styles/themes'

export const CreatorsHero: React.FC = () => (
  <StyledSection>
    <Container maxWidth="lg">
      <StyledGrid>
        <StyledTextColumn>
          <StyledEyebrow>
            <Icon icon={faSparkles} sx={{ fontSize: pxToRem(14), mr: 1 }} />
            For Storybook Creators
          </StyledEyebrow>
          <StyledHeadline variant="h1">
            Deliver client storybooks in
            {' '}
            <StyledAccent>minutes, not days</StyledAccent>
          </StyledHeadline>
          <StyledSubtitle variant="body1">
            Pay once for the editor freelancers use to ship personalised
            storybooks at scale. Generate consistent characters, lay out every
            spread in our Canva-style editor, and export print-ready 300 DPI
            PDFs. No Photoshop, no per-project licensing, no monthly seat fees.
          </StyledSubtitle>
          <StyledCtaRow>
            <StyledPrimaryCta primary size="large" href="#pricing">
              See pricing
              <Icon icon={faArrowRight} style={{ marginLeft: 8 }} />
            </StyledPrimaryCta>
            <StyledSecondaryCta outline size="large" href="/shop">
              Open the editor
            </StyledSecondaryCta>
          </StyledCtaRow>
          <StyledTrustRow>
            <StyledTrustDot />
            <Typography variant="body2">
              One-time payment. No subscription. Yours forever.
            </Typography>
          </StyledTrustRow>
        </StyledTextColumn>

        <StyledVisualColumn>
          <StyledStatCard>
            <StyledStatNumber>30 min</StyledStatNumber>
            <StyledStatLabel>From brief to print-ready PDF</StyledStatLabel>
          </StyledStatCard>
          <StyledStatCard>
            <StyledStatNumber>$79</StyledStatNumber>
            <StyledStatLabel>Pays for itself on book #1</StyledStatLabel>
          </StyledStatCard>
          <StyledStatCard>
            <StyledStatNumber>300 DPI</StyledStatNumber>
            <StyledStatLabel>Print-ready exports for hardcover</StyledStatLabel>
          </StyledStatCard>
        </StyledVisualColumn>
      </StyledGrid>
    </Container>
  </StyledSection>
)

const StyledSection = styled('section')(({ theme }) => ({
  background: 'linear-gradient(135deg, #FFFAF6 0%, #FFF0E2 55%, #FFE3D1 100%)',
  padding: theme.spacing(5, 0, 6),
  position: 'relative',
  overflow: 'hidden',

  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(8, 0, 10),
  },
}))

const StyledGrid = styled('div')(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(5),
  gridTemplateColumns: '1fr',
  alignItems: 'center',

  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: '1.2fr 1fr',
    gap: theme.spacing(6),
  },
}))

const StyledTextColumn = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: theme.spacing(2.5),
}))

const StyledEyebrow = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  backgroundColor: 'rgba(220, 174, 150, 0.18)',
  color: theme.palette.primary.main,
  padding: theme.spacing(0.75, 1.75),
  borderRadius: pxToRem(999),
  fontSize: pxToRem(13),
  fontWeight: 700,
  letterSpacing: pxToRem(1.2),
  textTransform: 'uppercase',
}))

const StyledHeadline = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: 700,
  fontSize: pxToRem(30),
  lineHeight: 1.15,

  [theme.breakpoints.up('sm')]: {
    fontSize: pxToRem(38),
  },

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(48),
  },
}))

const StyledAccent = styled('span')(({ theme }) => ({
  color: theme.palette.secondary.main,
}))

const StyledSubtitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  opacity: 0.85,
  fontSize: pxToRem(15),
  lineHeight: 1.55,
  maxWidth: pxToRem(540),

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(17),
  },
}))

const StyledCtaRow = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
  marginTop: theme.spacing(1),

  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
    alignItems: 'center',
  },
}))

const StyledPrimaryCta = styled(Button)(({ theme }) => ({
  color: theme.palette.secondary.main,
  fontWeight: 600,
  padding: theme.spacing(2, 4),
}))

const StyledSecondaryCta = styled(Button)(({ theme }) => ({
  fontWeight: 500,
  padding: theme.spacing(2, 3),
  borderColor: theme.palette.primary.main,
  color: theme.palette.primary.main,

  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: 'rgba(255,255,255,0.5)',
  },
}))

const StyledTrustRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  color: theme.palette.primary.main,
  opacity: 0.75,
  marginTop: theme.spacing(1),

  '& .MuiTypography-root': {
    fontSize: pxToRem(13),
  },
}))

const StyledTrustDot = styled('span')(({ theme }) => ({
  width: pxToRem(6),
  height: pxToRem(6),
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.main,
  opacity: 0.6,
  flexShrink: 0,
}))

const StyledVisualColumn = styled('div')(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(2),
  gridTemplateColumns: 'repeat(3, 1fr)',

  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: '1fr',
    gap: theme.spacing(2.5),
  },
}))

const StyledStatCard = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: theme.spacing(0.5),
  padding: theme.spacing(2.5, 2.5),
  borderRadius: pxToRem(16),
  backgroundColor: '#FFFFFF',
  border: '1px solid rgba(108, 60, 20, 0.10)',
  boxShadow: '0 8px 24px rgba(108, 60, 20, 0.08)',

  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(3, 3),
  },
}))

const StyledStatNumber = styled('div')(({ theme }) => ({
  fontSize: pxToRem(28),
  fontWeight: 700,
  color: theme.palette.primary.main,
  lineHeight: 1.1,

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(36),
  },
}))

const StyledStatLabel = styled('div')(({ theme }) => ({
  fontSize: pxToRem(12),
  color: theme.palette.primary.main,
  opacity: 0.75,
  lineHeight: 1.35,

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(13),
  },
}))
